import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";
import { SalesforceFieldSelect, SOURCE_TO_OBJECT, humanize } from "../FieldMappingModal";
import {
  BOX_TYPES,
  BOX_TYPE_LABELS,
  FIELD_STATE,
  SIGNER_ROLES,
  SIGNER_ROLE_LABELS,
  fieldState,
  filterFields,
  signersSummary,
} from "./templateStudioModel";

const Panel = styled.aside`
  width: 400px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #e2e8f0;
  min-height: 0;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$active ? "#1e293b" : "#64748b")};
  border-bottom: 2px solid ${(p) => (p.$active ? "#2563eb" : "transparent")};
  cursor: pointer;
`;

const Body = styled.div`
  flex: 1;
  overflow: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const Summary = styled.div`
  font-size: 13px;
  color: #334155;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  margin-right: 5px;
  vertical-align: middle;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 7px 9px;
  font-size: 13px;
`;

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 7px 8px;
  font-size: 13px;
  background: #fff;
  width: 100%;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldRow = styled.button`
  text-align: left;
  border: 1px solid ${(p) => (p.$selected ? "#2563eb" : "#e2e8f0")};
  background: ${(p) => (p.$selected ? "#eff6ff" : "#fff")};
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  display: grid;
  grid-template-columns: 14px 1fr auto;
  gap: 8px;
  align-items: start;
  font-size: 13px;
  color: #1e293b;
  &:hover {
    background: #f8fafc;
  }
`;

const FieldTitle = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FieldMeta = styled.div`
  font-size: 11.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PageChip = styled.span`
  font-size: 11px;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
`;

const Card = styled.div`
  border: 1px solid #bfdbfe;
  background: #f8fbff;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
`;

const SmallButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(SmallButton)`
  color: #b91c1c;
  border-color: #fecaca;
  &:hover {
    background: #fef2f2;
  }
`;

const TypeButton = styled.button`
  border: 1px solid ${(p) => (p.$active ? "#7c3aed" : "#ddd6fe")};
  background: ${(p) => (p.$active ? "#7c3aed" : "#f5f3ff")};
  color: ${(p) => (p.$active ? "#fff" : "#5b21b6")};
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const Note = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #475569;
  line-height: 1.45;
`;

const Empty = styled.div`
  padding: 20px 10px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
`;

const sourceLabel = (dataSources, value) => dataSources.find((d) => d.value === value)?.label || value;

const sfFieldLabel = (sfFieldsCache, source, name) => {
  const objectType = SOURCE_TO_OBJECT[source];
  return (sfFieldsCache[objectType] || []).find((f) => f.name === name)?.label || name;
};

export const FieldsTab = ({
  fields,
  mappings,
  templateType,
  selectedFieldName,
  onSelectField,
  onShowField,
  onUpdateMapping,
  onClearMapping,
  onRemoveField,
  removingField,
  onSuggest,
  suggesting,
  dataSources,
  transformations,
  sfFieldsCache,
  popularFieldsByObject,
}) => {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("all");
  const [confirmingRemove, setConfirmingRemove] = useState(null);

  const visible = useMemo(() => filterFields(fields, { query, mode, mappings }), [fields, query, mode, mappings]);
  const mappedCount = fields.filter((f) => fieldState(f, mappings) === "mapped").length;
  const offPage = fields.filter((f) => !f.rects || f.rects.length === 0).length;
  const selected = fields.find((f) => f.field_name === selectedFieldName) || null;
  const selectedMapping = selected ? mappings[selected.field_name] || {} : {};
  const objectType = SOURCE_TO_OBJECT[selectedMapping.sf_object];

  const groupedSources = useMemo(() => {
    const groups = [];
    dataSources.forEach((ds) => {
      const g = ds.group || "";
      let entry = groups.find((x) => x.group === g);
      if (!entry) {
        entry = { group: g, items: [] };
        groups.push(entry);
      }
      entry.items.push(ds);
    });
    return groups;
  }, [dataSources]);

  return (
    <>
      <Summary data-testid="studio-fields-summary">
        <span>
          <Dot $color={FIELD_STATE.mapped.color} />
          {mappedCount} mapped
        </span>
        <span>
          <Dot $color={FIELD_STATE.unmapped.color} />
          {fields.length - mappedCount} not mapped
        </span>
        {templateType === "pdf" && offPage > 0 && <span>· {offPage} not on the page</span>}
      </Summary>

      {selected && (
        <Card data-testid="studio-field-editor">
          <CardTitle>
            <span title={selected.field_name}>{humanize(selected.field_label || selected.field_name)}</span>
            <Row>
              {selected.rects?.length > 0 && (
                <SmallButton type="button" onClick={() => onShowField(selected.field_name)}>
                  Show on page
                </SmallButton>
              )}
              <SmallButton type="button" onClick={() => onSelectField(null)} aria-label="Close field">
                ✕
              </SmallButton>
            </Row>
          </CardTitle>
          <FieldMeta>
            Field name: {selected.field_name}
            {selected.rects?.length > 0
              ? ` · page ${[...new Set(selected.rects.map((r) => r.page_number))].join(", ")}`
              : templateType === "pdf"
                ? " · not found on the page"
                : ""}
          </FieldMeta>
          <Label>
            Where the value comes from
            <Select
              data-testid="studio-source-select"
              value={selectedMapping.sf_object || ""}
              onChange={(e) => onUpdateMapping(selected.field_name, "sf_object", e.target.value)}
            >
              <option value="">— Not mapped —</option>
              {groupedSources.map((g) =>
                g.group ? (
                  <optgroup key={g.group} label={g.group}>
                    {g.items.map((ds) => (
                      <option key={ds.value} value={ds.value}>
                        {ds.label}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  g.items.map((ds) => (
                    <option key={ds.value} value={ds.value}>
                      {ds.label}
                    </option>
                  ))
                )
              )}
            </Select>
          </Label>
          <Label>
            Salesforce field
            <SalesforceFieldSelect
              value={selectedMapping.sf_field_name || ""}
              onChange={(val) => onUpdateMapping(selected.field_name, "sf_field_name", val)}
              fields={sfFieldsCache[objectType] || []}
              disabled={!selectedMapping.sf_object}
              popularNames={popularFieldsByObject[objectType]}
            />
          </Label>
          <Label>
            Formatting
            <Select
              data-testid="studio-transform-select"
              value={selectedMapping.transformation || ""}
              disabled={!selectedMapping.sf_object}
              onChange={(e) => onUpdateMapping(selected.field_name, "transformation", e.target.value)}
            >
              {transformations.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Label>
          <Row style={{ justifyContent: "space-between" }}>
            <Tooltip title="Guess a Salesforce field from this field's name" arrow>
              <SmallButton type="button" onClick={() => onSuggest(selected.field_name)} disabled={suggesting}>
                {suggesting ? "Thinking…" : "Suggest"}
              </SmallButton>
            </Tooltip>
            <DangerButton
              type="button"
              onClick={() => onClearMapping(selected.field_name)}
              disabled={!selectedMapping.sf_object}
            >
              Clear mapping
            </DangerButton>
          </Row>
          {templateType === "pdf" && selected.rects?.length > 0 && onRemoveField && (
            confirmingRemove === selected.field_name ? (
              <Card as="div" style={{ borderColor: "#fecaca", background: "#fff7f7" }} data-testid="studio-remove-field-confirm">
                <Note>
                  Take the fillable box <strong>{humanize(selected.field_label || selected.field_name)}</strong> out of the
                  PDF? The template file is changed and saved to Formstack right away, and any mapping on this box is dropped.
                  Nothing printed on the page changes. To get the box back, replace the file with the original.
                </Note>
                <Row>
                  <DangerButton
                    type="button"
                    data-testid="studio-remove-field-yes"
                    disabled={removingField}
                    onClick={async () => {
                      // Stay open (disabled) until the request settles so a
                      // second click cannot start a second rewrite of the PDF.
                      await onRemoveField(selected.field_name);
                      setConfirmingRemove(null);
                    }}
                  >
                    {removingField ? "Removing…" : "Yes, remove it"}
                  </DangerButton>
                  <SmallButton type="button" data-testid="studio-remove-field-no" onClick={() => setConfirmingRemove(null)}>
                    Keep it
                  </SmallButton>
                </Row>
              </Card>
            ) : (
              <Row>
                <Tooltip title="Delete this fillable box from the PDF — for a box a signature spot already covers, or one nobody will fill" arrow>
                  <DangerButton
                    type="button"
                    data-testid="studio-remove-field"
                    disabled={removingField}
                    onClick={() => setConfirmingRemove(selected.field_name)}
                  >
                    Remove from PDF
                  </DangerButton>
                </Tooltip>
              </Row>
            )
          )}
        </Card>
      )}

      <Row>
        <Input
          placeholder="Search fields…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="studio-field-search"
        />
        <Select style={{ width: 150 }} value={mode} onChange={(e) => setMode(e.target.value)} data-testid="studio-field-filter">
          <option value="all">All fields</option>
          <option value="mapped">Mapped</option>
          <option value="unmapped">Not mapped</option>
          {templateType === "pdf" && <option value="offpage">Not on the page</option>}
        </Select>
      </Row>

      <List data-testid="studio-field-list">
        {visible.map((f) => {
          const state = fieldState(f, mappings);
          const m = mappings[f.field_name];
          const pagesOn = [...new Set((f.rects || []).map((r) => r.page_number))];
          return (
            <FieldRow
              key={f.field_name}
              type="button"
              $selected={selectedFieldName === f.field_name}
              data-testid="studio-field-row"
              data-field-name={f.field_name}
              onClick={() => onSelectField(f.field_name)}
            >
              <Dot $color={FIELD_STATE[state].color} style={{ marginTop: 3 }} />
              <div style={{ minWidth: 0 }}>
                <FieldTitle title={f.field_name}>{humanize(f.field_label || f.field_name)}</FieldTitle>
                <FieldMeta>
                  {m?.sf_object
                    ? `${sourceLabel(dataSources, m.sf_object)} → ${sfFieldLabel(sfFieldsCache, m.sf_object, m.sf_field_name)}`
                    : "Not mapped"}
                </FieldMeta>
              </div>
              {pagesOn.length > 0 ? (
                <PageChip>p. {pagesOn.join(", ")}</PageChip>
              ) : templateType === "pdf" ? (
                <PageChip title="Formstack lists this field but it is not a fillable box in the PDF">off page</PageChip>
              ) : null}
            </FieldRow>
          );
        })}
        {visible.length === 0 && <Empty>No fields match.</Empty>}
      </List>
    </>
  );
};

export const SignaturesTab = ({
  templateType,
  boxes,
  selectedBoxId,
  onSelectBox,
  onShowBox,
  onUpdateBox,
  onDeleteBox,
  placement,
  onArmPlacement,
  onCancelPlacement,
  canEdit,
  suggestedBoxes,
  onApplySuggestions,
  showHints,
  onToggleHints,
  signatureSource,
  poaStampCount = 0,
}) => {
  const [role, setRole] = useState(placement?.role || "client");
  const selected = boxes.find((b) => b.id === selectedBoxId) || null;
  const signers = signersSummary(boxes);

  if (templateType !== "pdf") {
    return (
      <>
        <Summary>
          {signatureSource === "tags" ? (
            <span>
              <Dot $color={FIELD_STATE.signature.color} />
              Signable — this Word file carries Formstack signature tags.
            </span>
          ) : (
            <span>
              <Dot $color={FIELD_STATE.unmapped.color} />
              No signature tags found in this Word file yet.
            </span>
          )}
        </Summary>
        <Note>
          Word templates are signed through tags typed into the document, such as <code>[sig|req|signer1]</code> for
          the client and <code>[sig|req|signer2]</code> for the attorney. The visual Word view and tag toolkit are the
          next step of this editor.
        </Note>
      </>
    );
  }

  return (
    <>
      <Summary data-testid="studio-signature-summary">
        {boxes.length ? (
          <span>
            <Dot $color={FIELD_STATE.signature.color} />
            Signable · {signers}
          </span>
        ) : (
          <span>
            <Dot $color={FIELD_STATE.unmapped.color} />
            No signature spots yet — documents made from this template cannot be sent for signature.
          </span>
        )}
      </Summary>

      {canEdit ? (
        <Card>
          <CardTitle>Add a signature spot</CardTitle>
          <Label>
            Who signs here
            <Select data-testid="studio-signer-select" value={role} onChange={(e) => setRole(e.target.value)}>
              {SIGNER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Label>
          <Row style={{ flexWrap: "wrap" }}>
            {BOX_TYPES.map((t) => (
              <TypeButton
                key={t.value}
                type="button"
                data-testid={`studio-add-${t.value}`}
                $active={placement?.fieldType === t.value && placement?.role === role}
                onClick={() =>
                  placement?.fieldType === t.value && placement?.role === role
                    ? onCancelPlacement()
                    : onArmPlacement({ role, fieldType: t.value })
                }
              >
                {t.label}
              </TypeButton>
            ))}
          </Row>
          {placement ? (
            <Note data-testid="studio-placement-hint">
              Click on the page where the <b>{SIGNER_ROLE_LABELS[placement.role]}</b>&apos;s{" "}
              <b>{BOX_TYPE_LABELS[placement.fieldType].toLowerCase()}</b> goes. Drag it afterwards to fine-tune;
              drag the corner to resize. <SmallButton type="button" onClick={onCancelPlacement}>Cancel</SmallButton>
            </Note>
          ) : (
            <Note>Pick who signs, then a type, then click the spot on the page.</Note>
          )}
          <Row style={{ justifyContent: "space-between", alignItems: "center" }}>
            <SmallButton
              type="button"
              data-testid="studio-suggest-boxes"
              disabled={!suggestedBoxes?.length}
              onClick={onApplySuggestions}
              title={
                suggestedBoxes?.length
                  ? "Reads the form's own labels (Signature, Date signed, Initials) and drops a box beside each"
                  : "No signature labels were found in this form's text"
              }
            >
              Suggest spots from the form ({suggestedBoxes?.length || 0})
            </SmallButton>
            <label style={{ fontSize: 12, color: "#475569", display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={showHints} onChange={onToggleHints} /> Show signature labels
            </label>
          </Row>
        </Card>
      ) : (
        <Note>Only people allowed to manage signature forms can add or move signature spots.</Note>
      )}

      {selected && (
        <Card data-testid="studio-box-editor">
          <CardTitle>
            <span>Selected spot</span>
            <Row>
              <SmallButton type="button" onClick={() => onShowBox(selected.id)}>
                Show on page
              </SmallButton>
              <SmallButton type="button" onClick={() => onSelectBox(null)} aria-label="Close spot">
                ✕
              </SmallButton>
            </Row>
          </CardTitle>
          <Row>
            <Label style={{ flex: 1 }}>
              Who signs
              <Select
                value={selected.role}
                disabled={!canEdit}
                onChange={(e) => onUpdateBox(selected.id, { role: e.target.value })}
              >
                {SIGNER_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </Label>
            <Label style={{ flex: 1 }}>
              Type
              <Select
                value={selected.field_type}
                disabled={!canEdit}
                onChange={(e) => onUpdateBox(selected.id, { field_type: e.target.value })}
              >
                {BOX_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Label>
          </Row>
          <FieldMeta>
            Page {selected.page_number} · {Math.round(selected.width)} × {Math.round(selected.height)} pt
            {selected.label ? ` · near "${selected.label}"` : ""}
          </FieldMeta>
          {canEdit && (
            <Row style={{ justifyContent: "flex-end" }}>
              <DangerButton type="button" data-testid="studio-delete-box" onClick={() => onDeleteBox(selected.id)}>
                Remove spot
              </DangerButton>
            </Row>
          )}
        </Card>
      )}

      {poaStampCount > 0 && (
        <Note>
          This template also carries {poaStampCount} power-of-attorney stamp placement{poaStampCount === 1 ? "" : "s"}; those
          are kept as they are.
        </Note>
      )}

      <List data-testid="studio-box-list">
        {boxes.map((b) => (
          <FieldRow
            key={b.id}
            type="button"
            $selected={selectedBoxId === b.id}
            data-testid="studio-box-row"
            onClick={() => onSelectBox(b.id)}
          >
            <Dot $color={FIELD_STATE.signature.color} style={{ marginTop: 3 }} />
            <div style={{ minWidth: 0 }}>
              <FieldTitle>
                {SIGNER_ROLE_LABELS[b.role] || b.role} · {BOX_TYPE_LABELS[b.field_type] || b.field_type}
              </FieldTitle>
              <FieldMeta>{b.label ? `near "${b.label}"` : "placed by hand"}</FieldMeta>
            </div>
            <PageChip>p. {b.page_number}</PageChip>
          </FieldRow>
        ))}
      </List>
    </>
  );
};

const StudioSidebar = ({ tab, onTabChange, fieldsCount, boxesCount, children }) => (
  <Panel data-testid="studio-sidebar">
    <Tabs>
      <Tab type="button" $active={tab === "fields"} onClick={() => onTabChange("fields")} data-testid="studio-tab-fields">
        Fields ({fieldsCount})
      </Tab>
      <Tab
        type="button"
        $active={tab === "signatures"}
        onClick={() => onTabChange("signatures")}
        data-testid="studio-tab-signatures"
      >
        Signatures ({boxesCount})
      </Tab>
    </Tabs>
    <Body>{children}</Body>
  </Panel>
);

export default StudioSidebar;
